import { Link } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { PrivacySettingsLink } from '../../-components/privacy-settings-link';
import { ArrowUpRight, Instagram } from 'lucide-react';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { DialogTrigger } from '@worldcoo/ui/components/dialog';
import { ComingSoonDialog } from './coming-soon-dialog';
import { DARK_FORM_FIELDS, pillSubmit, Squiggle } from './primitives';
import { lazy, Suspense } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { CTA_SURFACE, CtaWatermark } from './footer-watermark';
import { NewsletterForm } from './newsletter';
import { JOBS_URL, DONOR_APP_URL } from './external-links';
import { useNgoAreaUrl } from '@/lib/ngo-area';
import type { SolutionKey } from './solutions/solutions-data';
import type { ContactFormVariant } from '@/routes/-components/contact-form';
import { FooterHeart } from './lottie/footer-heart';
import { BCorpMark } from './bcorp-mark';

// Lazy, like `www` does it: the form pulls in zod, the form runtime and the terms dialog, none of which
// most visitors ever need — and on the pages without the form variant it is never requested at all.
const ContactForm = lazy(() => import('../../-components/contact-form').then((m) => ({ default: m.ContactForm })));

/** Solid LinkedIn glyph (lucide only ships the outline one) — filled badge in `currentColor`. */
function LinkedinFilled(props: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
			<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
		</svg>
	);
}

/** The pages of the redesigned site the footer points at. All of them exist. */
type InternalRoute =
	| '/new'
	| '/new/solutions'
	| '/new/partner-ngos'
	| '/new/about-us'
	| '/new/contact'
	| '/new/faq'
	| '/new/legal-disclaimer'
	| '/new/privacy-policy'
	| '/new/cookies'
	| '/new/selection-criteria';

/**
 * A footer entry, as a discriminated union.
 *
 * It used to be `{ label, to?, href? }` with an `href ?? '#'` fallback, which meant "no destination"
 * was a representable — and, for nine of the fifteen entries, actual — state: the review found every
 * one of them (TEC-3821 B4). Naming the three kinds makes a destination-less link fail to compile
 * instead of shipping.
 */
type FooterLink =
	| { kind: 'route'; label: string; to: InternalRoute; hash?: string }
	/** A channel of the Solutions page, opened directly on its tab. */
	| { kind: 'solution'; label: string; solution: SolutionKey }
	| { kind: 'external'; label: string; href: string }
	/**
	 * A destination that is announced but not published yet, so the entry opens a "coming soon"
	 * dialog instead of leaving the site. The Brandbook is the one case: its guidelines live on a
	 * staging site that is not public, and linking a footer entry at it was worse than saying so.
	 */
	| { kind: 'coming-soon'; label: string };

/**
 * Every interactive thing on the olive card shares one hover/focus colour: `brand`, the site's orange.
 *
 * Its contrast against the card's olive (#b0b86a) is only about 1.5:1, and four alternatives were
 * tried on that arithmetic — white, a lightened ink, the CTA band's near-black, and a darkened orange
 * — before the candidates were finally rendered on the card side by side and LOOKED at. The number is
 * misleading here: contrast ratio is a lightness measure, and orange against olive separates by HUE,
 * which it does not count. On the page the brand orange reads immediately as a state change, while the
 * darkened oranges that scored better go brown and stop reading as orange at all.
 *
 * So: judge this one on the rendered card, not on the ratio. It is a hover state on a decorative
 * surface, not body copy — but if it is ever reused where sustained reading matters, that trade is
 * back on the table.
 *
 * A LITERAL colour rather than an opacity step, because this class is shared by links that start at
 * full strength and by the legal row, which starts at `/70`: an opacity-based hover would land those
 * two on different colours, and on the legal row it would barely move at all.
 *
 * Focus gets it too — a keyboard reader needs the same signal the pointer gets.
 */
const FOOTER_LINK_CLASS = 'hover:text-brand focus-visible:text-brand transition-colors';

function FooterLink({ link }: { link: FooterLink }) {
	switch (link.kind) {
		case 'route':
			return (
				<Link to={link.to} hash={link.hash} className={FOOTER_LINK_CLASS}>
					{link.label}
				</Link>
			);
		case 'solution':
			// The hash as well as the search param: the tab opens, and the page lands on the section that
			// holds it instead of at the top, where the tabs are two screens down.
			return (
				<Link to="/new/solutions" search={{ solution: link.solution }} hash="solutions" className={FOOTER_LINK_CLASS}>
					{link.label}
				</Link>
			);
		case 'external':
			return (
				<a href={link.href} target="_blank" rel="noopener noreferrer" className={FOOTER_LINK_CLASS}>
					{link.label}
				</a>
			);
		case 'coming-soon':
			return <ComingSoonLink label={link.label} />;
	}
}

/**
 * A footer entry whose destination does not exist yet. Its own component rather than a branch of
 * `FooterLink`, because the dialog needs `useLingui` and hooks cannot live inside that switch.
 */
function ComingSoonLink({ label }: { label: string }) {
	const { t } = useLingui();
	return (
		<ComingSoonDialog description={t`We're finishing this section. It will be available here very soon.`}>
			<DialogTrigger className={`${FOOTER_LINK_CLASS} cursor-pointer text-left`}>{label}</DialogTrigger>
		</ComingSoonDialog>
	);
}

/**
 * The site footer. Two heads on one body: the default sends people off to the two entry points, while
 * `cta="form"` swaps that for the contact form the hand-off puts on the Solutions page. The green card
 * below is identical in both.
 *
 * `formVariant` is which questionnaire that form asks. It matters beyond the fields: the variant is the
 * `category` the backend stores and the heading Slack prints, so an NGO reaching the company form
 * arrives filed as a company lead.
 */
export function Footer(props: {
	cta?: 'links' | 'form';
	/**
	 * Which enquiry the `form` head asks for. Defaults to the company questionnaire, which is what
	 * Solutions wants — but the partner-NGO page was serving it too, so an entity reached the wrong
	 * questionnaire and arrived in Slack under `company` (TEC-3821 B13). It matters beyond the fields:
	 * the variant is the `category` the backend stores and the heading Slack prints. The audience also
	 * decides the pitch above the form: a form asking an NGO about its projects under a headline
	 * addressed to companies is only half fixed.
	 */
	formVariant?: Extract<ContactFormVariant, 'company' | 'ngo'>;
}) {
	const { t } = useLingui();
	const currentYear = new Date().getFullYear();
	// Per-environment, like every other sign-up CTA on the site — a hardcoded production URL would send
	// a visitor of `snd` to `pro`.
	const ngoArea = useNgoAreaUrl();

	const columns: { title: string; links: FooterLink[]; social?: boolean; bcorp?: boolean }[] = [
		{
			title: t`Company`,
			bcorp: true,
			links: [
				{ kind: 'route', label: t`About us`, to: '/new/about-us' },
				{ kind: 'coming-soon', label: t`Brandbook` },
				{ kind: 'external', label: t`Join our team`, href: JOBS_URL },
				{ kind: 'route', label: t`FAQ`, to: '/new/faq' },
				{ kind: 'route', label: t`Contact`, to: '/new/contact' },
			],
		},
		{
			title: t`Solutions`,
			social: true,
			links: [
				{ kind: 'solution', label: t`Charity rounding up`, solution: 'stores' },
				{ kind: 'solution', label: t`Solidarity euro`, solution: 'ecommerce' },
				{ kind: 'solution', label: t`Solidarity portals`, solution: 'portal' },
				{ kind: 'solution', label: t`Payroll rounding`, solution: 'payroll' },
				{ kind: 'route', label: t`Tech partners`, to: '/new/solutions', hash: 'tech-partners' },
			],
		},
		{
			title: t`NGO`,
			links: [
				{ kind: 'route', label: t`Partner NGOs`, to: '/new/partner-ngos' },
				{ kind: 'route', label: t`Selection process`, to: '/new/selection-criteria' },
				{ kind: 'external', label: t`NGO sign-up`, href: ngoArea('/onboarding') },
				{ kind: 'external', label: t`WebApp`, href: DONOR_APP_URL },
				{ kind: 'external', label: t`Certificates`, href: DONOR_APP_URL },
			],
		},
	];

	const formVariant = props.formVariant ?? 'company';

	// The squiggled word is the same in both, so only the sentence around it changes.
	const heading =
		formVariant === 'ngo' ? (
			<h2 className="text-4xl font-semibold md:text-5xl">
				<Trans>
					Shall we fund your next <Squiggle lineClassName="text-olive">project</Squiggle>?
				</Trans>
			</h2>
		) : (
			<h2 className="text-4xl font-semibold md:text-5xl">
				<Trans>
					Ready to make real <Squiggle lineClassName="text-olive">impact</Squiggle>?
				</Trans>
			</h2>
		);
	const blurb = (
		<p className="text-lg text-white/70">
			{formVariant === 'ngo'
				? t`Join the social entities that fund their projects through our partner companies' fundraising channels. Tell us about your project and we'll look for the opportunity that fits it.`
				: t`Join our network of companies turning every purchase into an opportunity for change. Tell us your case and we'll find the right solution for you.`}
		</p>
	);

	return (
		<footer className="text-ink">
			{props.cta === 'form' ? (
				<section className={cn(CTA_SURFACE, 'relative overflow-hidden px-6 py-20 text-white md:py-28')}>
					<CtaWatermark side="left" />
					<div className="relative mx-auto grid max-w-[1440px] items-start gap-12 lg:grid-cols-2 lg:gap-20 xl:px-10">
						<div className="flex flex-col gap-6">
							{heading}
							{blurb}
						</div>
						{/* The dark surface is applied from here rather than by forking the form — see
						    DARK_FORM_FIELDS. The submit comes from out here for the same reason: the shared
						    component's own button is the backoffice's, and on this band it has to be the site's
						    white pill, exactly as it is on the Contact page's panel. Same `pillSubmit` call, so
						    the two cannot drift; the size is the site's default because these fields are the
						    shared component's default, which the Contact panel scales up and this band does not. */}
						<div className={DARK_FORM_FIELDS}>
							<Suspense fallback={<div className="min-h-[520px]" />}>
								<ContactForm variant={formVariant} submit={pillSubmit(t`Send message`)} />
							</Suspense>
						</div>
					</div>
				</section>
			) : (
				<section className={cn(CTA_SURFACE, 'relative overflow-hidden px-6 py-20 text-center text-white md:py-28')}>
					<CtaWatermark side="right" />
					<div className="relative mx-auto flex max-w-3xl flex-col items-center gap-6">
						{heading}
						{blurb}
						<div className="flex flex-wrap justify-center gap-4">
							<Link
								to="/new/contact/$audience"
								params={{ audience: 'company' }}
								className="flex items-center gap-2 rounded-full bg-white py-2.5 pr-2.5 pl-5 font-medium text-black transition-opacity hover:opacity-90"
							>
								{t`For companies`}
								<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
									<ArrowUpRight className="size-4" />
								</span>
							</Link>
							<Link
								to="/new/partner-ngos"
								className="flex items-center gap-2 rounded-full bg-white py-2.5 pr-2.5 pl-5 font-medium text-black transition-opacity hover:opacity-90"
							>
								{t`For NGOs`}
								<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
									<ArrowUpRight className="size-4" />
								</span>
							</Link>
						</div>
					</div>
				</section>
			)}

			<div className={cn(CTA_SURFACE, 'px-4 pb-4')}>
				<div className="bg-olive text-olive-foreground relative mx-auto max-w-[1440px] overflow-hidden rounded-3xl px-8 py-12 md:px-14 md:py-16">
					{/* Plays once, when the footer is reached, and holds its last frame — then answers the
					    cursor: `FooterHeart` leans the hand toward the heart on hover and taps it red on
					    click. It loops no more than it did before; a drawing that rebuilt itself forever in
					    the corner of every page pulled the eye away from the links it sits next to.
					    `playWhenVisible` is what keeps a one-shot from being over before the reader gets
					    here: the footer is the last thing on every page, so it would otherwise have played
					    out on load, several screens away.

					    `pointer-events-none` is gone, and has to be — it is what the hover reads. It cost
					    nothing to drop: the columns beside it reserve this corner with `lg:pr-56`/`xl:pr-72`,
					    so there is no link underneath for the box to swallow. */}
					<FooterHeart
						ariaLabel={t`A finger touching a heart`}
						className="absolute top-10 right-6 hidden w-52 lg:block xl:right-10 xl:w-64"
					/>

					<div className="grid gap-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:pr-56 xl:pr-72">
						<div className="flex flex-col gap-6">
							<Link to="/new" aria-label={t`Worldcoo home`}>
								<WorldcooLogo className="h-12 w-auto" accentClassName="text-ink" />
							</Link>
							<NewsletterForm />
						</div>

						{columns.map((col) => (
							<nav key={col.title} className="flex flex-col gap-3 text-sm">
								<p className="text-olive-foreground/60 font-semibold">{col.title}</p>
								<ul className="flex flex-col gap-2.5">
									{col.links.map((link) => (
										<li key={link.label}>
											<FooterLink link={link} />
										</li>
									))}
								</ul>
								{col.bcorp && (
									<a
										href="https://www.bcorporation.net/en-us/find-a-b-corp/company/worldcoo"
										target="_blank"
										rel="noopener noreferrer"
										// Takes the same hover/focus colour as every other entry on the card, which is only
										// possible because the mark is inline SVG on `currentColor`: as a bitmap it could
										// merely be darkened with a `brightness` filter, which was never the link colour.
										className={cn('mt-8 w-fit', FOOTER_LINK_CLASS)}
									>
										<span className="sr-only">{t`B Corp Certified`}</span>
										{/* 96px, the size `www`'s footer draws the same mark at — the 64px the bitmap used was
										    the smaller of the two and read as an afterthought beside the column of links. */}
										<BCorpMark className="h-24 w-auto" />
									</a>
								)}
								{col.social && (
									<div className="mt-8 flex flex-col gap-3">
										<p className="text-olive-foreground/60 font-semibold">{t`Follow us`}</p>
										<div className="flex gap-4">
											<a href="https://www.instagram.com/worldcoo/" target="_blank" rel="noopener noreferrer">
												<span className="sr-only">Instagram</span>
												<Instagram className={cn(FOOTER_LINK_CLASS, 'size-5')} />
											</a>
											<a href="https://www.linkedin.com/company/worldcoo" target="_blank" rel="noopener noreferrer">
												<span className="sr-only">LinkedIn</span>
												<LinkedinFilled className={cn(FOOTER_LINK_CLASS, 'size-5')} />
											</a>
										</div>
									</div>
								)}
							</nav>
						))}
					</div>

					<div className="border-olive-foreground/20 text-olive-foreground/70 relative mt-12 flex flex-wrap items-center justify-between gap-3 border-t pt-6 text-xs">
						<p className="flex flex-wrap items-center gap-x-3 gap-y-1">
							<span>
								&copy; {currentYear} Worldcoo. {t`All rights reserved`}
							</span>
							<Link to="/new/legal-disclaimer" className={FOOTER_LINK_CLASS}>
								{t`Terms of use`}
							</Link>
							<span aria-hidden>·</span>
							<Link to="/new/privacy-policy" className={FOOTER_LINK_CLASS}>
								{t`Privacy Policy`}
							</Link>
							<span aria-hidden>·</span>
							<Link to="/new/cookies" className={FOOTER_LINK_CLASS}>
								{t`Cookies`}
							</Link>
							<span aria-hidden>·</span>
							<PrivacySettingsLink className={FOOTER_LINK_CLASS} />
						</p>
					</div>
				</div>
			</div>
		</footer>
	);
}
